import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Video, Plus, Mail } from "lucide-react";

export const DashboardHeader = ({
  user,
  onSendReminders,
  showMeetingDialog,
  setShowMeetingDialog,
  meetingForm,
  setMeetingForm,
  onboardedClients,
  onScheduleMeeting,
  showWorkshopDialog,
  setShowWorkshopDialog,
  workshopForm,
  setWorkshopForm,
  onCreateWorkshop
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
      <div>
        <h1 className="text-3xl font-semibold text-neutral-900 mb-1">Consultant Dashboard</h1>
        <p className="text-neutral-500">Manage clients, billing, analytics & operations</p>
      </div>
      
      <div className="flex items-center gap-2 mt-4 md:mt-0">
        <Button 
          variant="outline"
          onClick={onSendReminders}
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          data-testid="send-reminders-btn"
        >
          <Bell className="w-4 h-4 mr-2" />
          Send Reminders
        </Button>
        
        <Dialog open={showMeetingDialog} onOpenChange={setShowMeetingDialog}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="schedule-meeting-btn">
              <Video className="w-4 h-4 mr-2" />
              Schedule Meeting
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Schedule 1:1 Meeting</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select 
                  value={meetingForm.client_id} 
                  onValueChange={(value) => setMeetingForm({ ...meetingForm, client_id: value })}
                >
                  <SelectTrigger data-testid="meeting-client-select">
                    <SelectValue placeholder="Select a client" />
                  </SelectTrigger>
                  <SelectContent>
                    {onboardedClients.map(client => (
                      <SelectItem key={client.user_id} value={client.user_id}>
                        {client.name} ({client.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Meeting Type</Label>
                <Select 
                  value={meetingForm.meeting_type} 
                  onValueChange={(value) => setMeetingForm({ ...meetingForm, meeting_type: value })}
                >
                  <SelectTrigger data-testid="meeting-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="strategy">Strategy Session</SelectItem>
                    <SelectItem value="check-in">Check-in Call</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="review">Quarterly Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={meetingForm.title}
                  onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                  placeholder="e.g., Q1 Strategy Session"
                  data-testid="meeting-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Textarea
                  value={meetingForm.description}
                  onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
                  placeholder="Topics to discuss..."
                  data-testid="meeting-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={meetingForm.date}
                    onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
                    data-testid="meeting-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={meetingForm.time}
                    onChange={(e) => setMeetingForm({ ...meetingForm, time: e.target.value })}
                    data-testid="meeting-time-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={meetingForm.duration_minutes}
                  onChange={(e) => setMeetingForm({ ...meetingForm, duration_minutes: parseInt(e.target.value) })}
                  data-testid="meeting-duration-input"
                />
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <Mail className="w-4 h-4 inline mr-2" />
                Client will receive an email notification with meeting details
                {user?.meeting_link && " and your meeting link"}
              </div>
              <Button 
                onClick={onScheduleMeeting}
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={!meetingForm.client_id || !meetingForm.title || !meetingForm.date || !meetingForm.time}
                data-testid="confirm-schedule-meeting-btn"
              >
                Schedule Meeting
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showWorkshopDialog} onOpenChange={setShowWorkshopDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#333333] hover:bg-[#444444] text-white" data-testid="create-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Create New Event</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4 overflow-y-auto flex-1 pr-2">
              <div className="space-y-2">
                <Label>Event Type</Label>
                <Select 
                  value={workshopForm.type} 
                  onValueChange={(value) => setWorkshopForm({ ...workshopForm, type: value })}
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workshop">Workshop</SelectItem>
                    <SelectItem value="meeting">Group Meeting</SelectItem>
                    <SelectItem value="call">Group Call</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={workshopForm.title}
                  onChange={(e) => setWorkshopForm({ ...workshopForm, title: e.target.value })}
                  placeholder="Event title"
                  data-testid="event-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={workshopForm.description}
                  onChange={(e) => setWorkshopForm({ ...workshopForm, description: e.target.value })}
                  placeholder="Event description"
                  rows={2}
                  data-testid="event-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={workshopForm.date}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, date: e.target.value })}
                    data-testid="event-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time *</Label>
                  <Input
                    type="time"
                    value={workshopForm.time}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, time: e.target.value })}
                    data-testid="event-time-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration (min)</Label>
                  <Input
                    type="number"
                    value={workshopForm.duration_minutes}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, duration_minutes: parseInt(e.target.value) })}
                    data-testid="event-duration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={workshopForm.location}
                    onChange={(e) => setWorkshopForm({ ...workshopForm, location: e.target.value })}
                    placeholder="Online or Address"
                    data-testid="event-location-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Event/Meeting Link</Label>
                <Input
                  value={workshopForm.meeting_link}
                  onChange={(e) => setWorkshopForm({ ...workshopForm, meeting_link: e.target.value })}
                  placeholder="https://zoom.us/j/..."
                  data-testid="event-meeting-link-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Link (optional)</Label>
                <Input
                  value={workshopForm.payment_link}
                  onChange={(e) => setWorkshopForm({ ...workshopForm, payment_link: e.target.value })}
                  placeholder="https://stripe.com/pay/..."
                  data-testid="event-payment-link-input"
                />
              </div>
              <Button 
                onClick={onCreateWorkshop}
                className="w-full bg-[#333333] hover:bg-[#444444] text-white sticky bottom-0"
                disabled={!workshopForm.title || !workshopForm.date || !workshopForm.time}
                data-testid="save-event-btn"
              >
                Create Event
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default DashboardHeader;
